import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_ADMIN_DASHBOARD, GET_AUDIT_LOGS } from '../graphql/admin';
import { AdminLayout } from '../components/AdminLayout';

export function AdminDashboardPage() {
  const { data: dashboardData, loading } = useQuery(GET_ADMIN_DASHBOARD);
  const { data: auditData } = useQuery(GET_AUDIT_LOGS, {
    variables: {
      filters: {
        page: 1,
        pageSize: 5,
      },
    },
  });

  const dashboard = dashboardData?.adminDashboard;
  const recentLogs = auditData?.adminAuditLogs || [];

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-8"></div>
            <div className="grid grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600 mt-1">
            Last updated: {dashboard?.lastUpdated ? new Date(dashboard.lastUpdated).toLocaleString() : 'Never'}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatNumber(dashboard?.totalUsers || 0)}
                </p>
                <p className={`text-sm mt-2 ${
                  (dashboard?.userGrowthRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatPercent(dashboard?.userGrowthRate || 0)} ↗
                </p>
              </div>
              <div className="text-4xl">👥</div>
            </div>
          </div>

          {/* Total Organizations */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Organizations</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatNumber(dashboard?.totalOrganizations || 0)}
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  +{dashboard?.newOrganizationsToday || 0} today
                </p>
              </div>
              <div className="text-4xl">🏢</div>
            </div>
          </div>

          {/* Active Subscriptions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Subs</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatNumber(dashboard?.activeSubscriptions || 0)}
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Paying customers
                </p>
              </div>
              <div className="text-4xl">💳</div>
            </div>
          </div>

          {/* MRR */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">MRR</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatCurrency(dashboard?.mrrCents || 0)}
                </p>
                <p className={`text-sm mt-2 ${
                  (dashboard?.revenueGrowthRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatPercent(dashboard?.revenueGrowthRate || 0)} ↗
                </p>
              </div>
              <div className="text-4xl">💰</div>
            </div>
          </div>
        </div>

        {/* Today's Activity */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Today's Activity</h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                +{dashboard?.newUsersToday || 0}
              </p>
              <p className="text-sm text-gray-600 mt-1">New Users</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                +{dashboard?.newOrganizationsToday || 0}
              </p>
              <p className="text-sm text-gray-600 mt-1">New Organizations</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">
                {formatNumber(dashboard?.activeUsersToday || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Active Users (DAU)</p>
            </div>
          </div>
        </div>

        {/* Plan Distribution & Engagement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Plan Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Plan Distribution</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Free</span>
                  <span className="text-sm font-semibold">{dashboard?.freePlanCount || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gray-500 h-2 rounded-full"
                    style={{
                      width: `${((dashboard?.freePlanCount || 0) / (dashboard?.totalOrganizations || 1)) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Pro</span>
                  <span className="text-sm font-semibold">{dashboard?.proPlanCount || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{
                      width: `${((dashboard?.proPlanCount || 0) / (dashboard?.totalOrganizations || 1)) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Enterprise</span>
                  <span className="text-sm font-semibold">{dashboard?.enterprisePlanCount || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{
                      width: `${((dashboard?.enterprisePlanCount || 0) / (dashboard?.totalOrganizations || 1)) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Engagement Metrics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Engagement</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Avg Contexts per Org</span>
                <span className="text-lg font-bold">{dashboard?.avgContextsPerOrg?.toFixed(1) || '0.0'}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Avg Projects per Org</span>
                <span className="text-lg font-bold">{dashboard?.avgProjectsPerOrg?.toFixed(1) || '0.0'}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Churn Rate</span>
                <span className={`text-lg font-bold ${
                  (dashboard?.churnRate || 0) > 5 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {dashboard?.churnRate?.toFixed(1) || '0.0'}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity (Audit Log) */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
            <a href="/admin/audit" className="text-sm text-blue-600 hover:underline">
              View All →
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Time</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Admin</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Action</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Resource</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-500">
                      No recent activity
                    </td>
                  </tr>
                ) : (
                  recentLogs.map((log: any) => (
                    <tr key={log.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(log.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="py-3 px-4 text-sm">{log.adminEmail}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {log.resourceType}: {log.resourceId || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
